import { useEffect, useState } from 'react';
import { NewsFeedItem } from '../../../app/services/api/news/newsConsumerTypes';
import { useNewsConsumerClient } from '../../../hooks/api/useNewsConsumerClient';

interface UseDashboardNewsResult {
  pinnedItems: NewsFeedItem[];
  otherItems: NewsFeedItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

interface StoreContext {
  storeNumber?: string;
  franchiseId?: number;
}

export function useDashboardNews(storeContext?: StoreContext, enabled = true): UseDashboardNewsResult {
  const client = useNewsConsumerClient();
  const storeNumber = storeContext?.storeNumber;
  const [pinnedItems, setPinnedItems] = useState<NewsFeedItem[]>([]);
  const [otherItems, setOtherItems] = useState<NewsFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (!enabled || !storeNumber) {
      setPinnedItems([]);
      setOtherItems([]);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }

    async function fetchNews() {
      setLoading(true);
      setError(null);

      try {
        const result = await client.getFeed({
          mode: 'Current',
          storeNumber,
          page: 1,
          pageSize: 12,
        });

        if (cancelled) return;

        setPinnedItems(result.items.filter(item => item.isPinned));
        setOtherItems(result.items.filter(item => !item.isPinned));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load news');
        setPinnedItems([]);
        setOtherItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchNews();

    return () => {
      cancelled = true;
    };
  }, [client, enabled, storeNumber, storeContext?.franchiseId, refreshCounter]);

  return {
    pinnedItems,
    otherItems,
    loading,
    error,
    refresh: () => setRefreshCounter(c => c + 1),
  };
}
